import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const About = () => {
  const { t } = useTranslation();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [whyChooseUs, setWhyChooseUs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleSections, setVisibleSections] = useState({});

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.15, rootMargin: '-80px' }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, whyRes] = await Promise.all([
          axios.get('/api/company'),
          axios.get('/api/company/why-choose-us')
        ]);
        setCompanyInfo(companyRes.data.data);
        setWhyChooseUs(whyRes.data.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Use static data as fallback
        setCompanyInfo(staticCompanyInfo);
        setWhyChooseUs(staticWhyChooseUs);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const staticCompanyInfo = {
    name: "ALMED EQUIPMENT",
    tagline: t('about.tagline'),
    established: 2005,
    yearsInBusiness: 15,
    description: "As manufacturer and dealer of healthcare services throughout India from last 15 years. ALMED EQUIPMENT is one of the prominent ISO CERTIFIED COMPANY which is continued to be trendsetter throughout the years of history.",
    aboutSummary: t('about.story.p2'),
    certifications: ["ISO 9001:2008 CERTIFIED COMPANY", "NSIC Certified", "MSME Certified"],
    awards: [
      "8th MT India Healthcare Awards 2018",
      "Voice of healthcare 2nd International Conference 2018",
      "WORLDCON 2017",
      "Goa healthcare excellence Award-2017",
      "AMOGS 2017"
    ],
    statistics: { satisfiedCustomers: "200+", yearsExperience: "15+" }
  };

  const staticWhyChooseUs = [
    { id: 1, title: t('whyChoose.quality.title'), description: t('whyChoose.quality.description'), icon: '✓' },
    { id: 2, title: t('whyChoose.patented.title'), description: t('whyChoose.patented.description'), icon: '📜' },
    { id: 3, title: t('whyChoose.service.title'), description: t('whyChoose.service.description'), icon: '🔧' },
    { id: 4, title: t('whyChoose.costEfficient.title'), description: t('whyChoose.costEfficient.description'), icon: '💰' },
    { id: 5, title: t('whyChoose.excellence.title'), description: t('whyChoose.excellence.description'), icon: '🎯' },
  ];

  const displayCompany = companyInfo || staticCompanyInfo;
  const displayWhyChooseUs = whyChooseUs.length > 0 ? whyChooseUs : staticWhyChooseUs;

  if (loading) {
    return (
      <div className="loading" style={{minHeight: '100vh', marginTop: '70px'}}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <p className="section-subtitle">{t('about.badge')}</p>
          <h1 style={{marginBottom: '1rem'}}>
            {t('about.title')}
          </h1>
          <p className="about-hero-tagline" style={{fontSize: '1.25rem', maxWidth: '700px'}}>
            {t('about.tagline')}
          </p>

          <div className="about-stats">
            <div className="stat-item">
              <div className="stat-number">{displayCompany.statistics.yearsExperience}</div>
              <div className="stat-label">{t('about.stats.experience')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{displayCompany.statistics.satisfiedCustomers}</div>
              <div className="stat-label">{t('about.stats.customers')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">{t('about.stats.support')}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">3</div>
              <div className="stat-label">{t('about.stats.certifications')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section id="story" className="section animate-section">
        <div className="container">
          <div 
            className="about-content"
            style={{
              opacity: visibleSections.story ? 1 : 0,
              transform: visibleSections.story ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <div>
              <h2 className="section-title" style={{textAlign: 'left'}}>{t('about.story.title')}</h2>
              <p style={{marginBottom: '1rem'}}>
                {t('about.story.p1')}
              </p>
              <p style={{marginBottom: '1rem'}}>
                {t('about.story.p2')}
              </p>
              <p>
                {t('about.story.p3')}
              </p>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary-color) 100%)',
              borderRadius: 'var(--radius-lg)',
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '4rem'
            }}>
              🏭
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section id="certifications" className="section animate-section" style={{backgroundColor: 'var(--gray-100)'}}>
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.certifications ? 1 : 0,
              transform: visibleSections.certifications ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <p className="section-subtitle">{t('about.certifications.badge')}</p>
            <h2 className="section-title">{t('about.certifications.title')}</h2>
          </div>

          <div className="discover-grid" style={{maxWidth: '800px', margin: '0 auto'}}>
            {displayCompany.certifications.map((cert, index) => (
              <div key={index} className="discover-card" style={{textAlign: 'center'}}>
                <div className="discover-card-content">
                  <div style={{fontSize: '3rem', marginBottom: '1rem'}}>🏅</div>
                  <h3 className="discover-card-title">{cert}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section id="awards" className="section animate-section">
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.awards ? 1 : 0,
              transform: visibleSections.awards ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <p className="section-subtitle">{t('about.awards.badge')}</p>
            <h2 className="section-title">{t('about.awards.title')}</h2>
            <p className="section-description">
              {t('about.awards.description')}
            </p>
          </div>

          <div className="products-grid">
            {displayCompany.awards.map((award, index) => (
              <div key={index} className="product-card">
                <div className="product-card-content" style={{textAlign: 'center'}}>
                  <div style={{fontSize: '3rem', marginBottom: '1rem'}}>🏆</div>
                  <h3 className="product-card-title">{award}</h3>
                  <p className="product-card-description">
                    Awarded for INNOVATIVE SOLUTIONS FOR INFECTION CONTROL
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="whychoose" className="section why-choose-section animate-section">
        <div className="container">
          <div 
            className="section-header"
            style={{
              opacity: visibleSections.whychoose ? 1 : 0,
              transform: visibleSections.whychoose ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <p className="section-subtitle" style={{color: 'rgba(255,255,255,0.8)'}}>{t('about.whyChoose.badge')}</p>
            <h2 className="section-title">{t('about.whyChoose.title')}</h2>
          </div>

          <div className="why-choose-grid">
            {displayWhyChooseUs.map((item) => (
              <div key={item.id} className="why-choose-card">
                <div className="why-choose-icon">
                  {item.icon || '✓'}
                </div>
                <h3 className="why-choose-title">{item.title}</h3>
                <p className="why-choose-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="cta-section animate-section">
        <div 
          className="container"
          style={{
            opacity: visibleSections.cta ? 1 : 0,
            transform: visibleSections.cta ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <h2 className="cta-title">{t('about.partner.title')}</h2>
          <p className="cta-description">
            {t('about.partner.description')}
          </p>
          <Link to="/contact" className="btn btn-white">{t('about.partner.contactUs')}</Link>
        </div>
      </section>
    </div>
  );
};

export default About;
